import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../components/elements/buttons";
import Input from "../../components/elements/inputs/Input";
import SwitchToggle from "../../components/elements/switchtoggle/SwitchToggle";
import useCreate from "../../hooks/useCreate";
import CategoryStatusUpdate from "./CategoryStatusUpdate";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { generateSlug } from "../../utilities";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams } from "react-router-dom";
import useCreateCategory from "../../hooks/category/useCreateCategory";
import useGetCategoryList from "../../hooks/category/useGetCategoryList";
import CircularProgress from "@mui/material/CircularProgress";
import { Controller, useForm } from "react-hook-form";
import { SLUG_SPECIAL_CHAR } from "../../config";
import useRead from "../../hooks/useRead";
import { FileUploader } from "react-drag-drop-files";
import useUploadMedia from "../../hooks/useUploadMedia";
import Spinner from "../../components/spinner";
import { toast } from "react-hot-toast";

const fileTypes = ["JPG", "PNG", "JPEG", "WEBP"];

const CategoryForm = forwardRef(({ data }, ref) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedParent, setSelectedParent] = useState(null);
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const navigate = useNavigate();

  const { control, setValue, handleSubmit, reset, getValues, clearErrors } =
    useForm({
      defaultValues: {
        status: true,
        name: "",
        slug: "",
        category_code: "",
        category_image: "",
        seo: {
          meta_title: "",
          meta_keywords: "",
          meta_desc: "",
        },
        parent_id: null,
        catalog_type: "",
        primary_menu: false,
        carousel_menu: false,
        show_in_category: false,
      },
    });

  const handleResetForm = () => {
    reset();
  };
  useImperativeHandle(ref, () => ({
    handleResetForm,
  }));
  const handleForm = (name, value) => {
    if (name === "name") {
      const slugValue = generateSlug(value);
      setValue("slug", slugValue);
    }
  };

  // Update handleCategorySubmit to use the correct payload
  const { handleSubmit: handleCategorySubmit } = useCreateCategory({
    reset: () => {
      reset();
      setImagePreview("");
    },
  });
  const { data: list } = useGetCategoryList();

  const {
    data: singleCategoryData,
    paramsObject: singleCategoryParams,
    setGetListParams: setSingleCategoryParams,
  } = useRead({
    url: "category/view",
    initialData: mode === "edit" ? { id: categoryId } : {},
    enabled: mode === "edit",
    method: "GET",
    onSuccess: (res) => {
      let resData = res.data;
      Object.keys(resData).forEach((key) => {
        if (key === "seo" && resData.seo && typeof resData.seo === "object") {
          setValue("seo.meta_title", resData.seo.meta_title || "");
          setValue("seo.meta_keywords", resData.seo.meta_keywords || "");
          setValue("seo.meta_desc", resData.seo.meta_desc || "");
        } else {
          setValue(key, resData[key]);
        }
      });
      setInputValue(resData.parent_tree || "");
      // Store parent_id for later reference
      if (resData.parent_id) {
        setSelectedParent({ id: resData.parent_id, name: resData.parent_tree });
      }
    },
  });

  const onChangeSetCatalogType = (value) => {
    if (value.catalog_type) {
      setValue("catalog_type", value.catalog_type);
    }
  };

  // Find parent category by ID in the list
  useEffect(() => {
    if (mode === "edit" && list?.data?.data && getValues("parent_id")) {
      const parentCategory = list.data.data.find(
        (cat) => cat.id === getValues("parent_id")
      );
      if (parentCategory) {
        setSelectedParent(parentCategory);
      }
    }
  }, [list?.data?.data, mode]);

  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [imageId, setImageId] = useState("");
  const [imageAlt, setImageAlt] = useState("");

  // Image upload handlers
  const handleMediaUploadSuccess = (data) => {
    setImageId(data.data.image_id);
    setImagePreview(data.data.image_url);
    setValue("category_image", data.data.image_id);
    // Do not set default alt text
    setImageUploading(false);
    toast.success("Image uploaded successfully", {
      position: "top-right",
      duration: 2000,
    });
  };

  const handleMediaUploadError = (error) => {
    setImageUploading(false);
    toast.error(error?.response?.data?.message || "Error uploading image", {
      position: "top-right",
      duration: 3000,
    });
  };

  const { upload } = useUploadMedia({
    url: "upload/image",
    onSuccess: handleMediaUploadSuccess,
    onError: handleMediaUploadError,
  });

  const handleImageUpload = (file) => {
    setImageUploading(true);
    upload.mutate({ thumbnail: file, screen: "CATEGORY" });
  };

  const handleReUpload = () => {
    setImagePreview("");
    setImageId("");
    setValue("category_image", "");
  };

  useEffect(() => {
    if (mode === "edit" && singleCategoryData?.data?.data?.thumbnail) {
      const thumb = singleCategoryData.data.data.thumbnail;
      if (thumb.image_path) {
        setImagePreview(thumb.image_path);
        setImageId(thumb.image_id);
        setValue("category_image", thumb.image_id);
      }
      setImageAlt(thumb.image_alt || "");
    }
    // eslint-disable-next-line
  }, [singleCategoryData?.data?.data?.thumbnail, mode]);

  // Submit handler to build payload and submit
  const onSubmit = (formData) => {
    const payload = {
      id: mode === "edit" ? getValues("id") || categoryId : "",
      name: formData.name,
      slug: formData.slug,
      title: "",
      seo: {
        meta_title: formData.seo?.meta_title || "",
        meta_keywords: formData.seo?.meta_keywords || "",
        meta_desc: formData.seo?.meta_desc || "",
      },
      primary_menu: formData.primary_menu || false,
      carousel_menu: formData.carousel_menu || false,
      show_in_category: formData.show_in_category || false,
      parent_id: formData.parent_id,
      thumbnail: {
        image_id: imageId,
        image_alt: imageAlt,
      },
    };
    handleCategorySubmit(payload);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className=" space-x-2 flex items-center"
          >
            <ArrowBackIcon className="w-6 h-6" />
            <span>Back</span>
          </button>
          <div className="flex justify-end mt-5 space-x-3">
            <PrimaryButton label={"Save"} type={"submit"} />
          </div>
        </div>
        {mode === "edit" ? (
          <CategoryStatusUpdate
            status={getValues("status")}
            categoryId={getValues("id") || categoryId}
          />
        ) : (
          <div className=" -ml-4 mt-4">
            <Controller
              name="status"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div>
                  <SwitchToggle
                    checkedValue={value}
                    label={"Enable Category"}
                    onChange={(e) => onChange(e)}
                    name="status"
                  />
                </div>
              )}
              rules={{ required: "Name is required" }}
            />
            <small className="block text-gray-500 ml-4 italic font-semibold relative -top-2">
              Store Front
            </small>
          </div>
        )}
        <div>
          <div className="mb-6 mt-6">
            <Controller
              name="name"
              control={control}
              rules={{
                required: "Category Name is required",
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <div>
                  <Input
                    label={"Category Name"}
                    placeholder={"e.g. Clothing"}
                    // required={"required"}
                    value={value}
                    error={error}
                    onChange={(e) => {
                      onChange(e);
                      handleForm("name", e.target.value);
                      clearErrors("slug");
                    }}
                  />
                </div>
              )}
            />
          </div>
          <div className="mb-6 mt-6">
            <Controller
              name="slug"
              control={control}
              rules={{
                required: "Slug is required",
                pattern: {
                  value: /^[A-Za-z0-9-]+$/,
                  message:
                    "Invalid Slug. Only letters, numbers, and hyphens are allowed.",
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <>
                  <Input
                    label={"Category Slug"}
                    // required={"required"}
                    placeholder={"Category Slug"}
                    value={value}
                    onChange={onChange}
                    error={error}
                  />
                </>
              )}
            />
          </div>
          {/* <div className="mb-6 mt-6">
                        <Controller
                            name="category_code"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Input
                                    label={"Category Code"}
                                    placeholder={"Category Code"}
                                    value={value}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </div> */}
          <div>
            <div className="md:flex md:items-center mb-6">
              <div className="md:w-1/3">
                <label className="label" htmlFor={"parent"}>
                  Parent
                </label>
              </div>
              <div className="md:w-2/3">
                {mode === "edit" && (
                  <div className=" flex items-center space-x-2">
                    <p>Current: </p>
                    <small className=" text-gray-500">
                      {getValues("parent_tree") || ""}
                    </small>
                  </div>
                )}

                <Controller
                  name="parent_id"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Autocomplete
                      fullWidth
                      freeSolo
                      disablePortal
                      value={selectedParent}
                      disabled={mode === "edit" ? true : false}
                      loading={list.isLoading}
                      onChange={(event, newValue) => {
                        onChange(newValue ? newValue.id : null);
                        setSelectedParent(newValue);
                        onChangeSetCatalogType(newValue);
                      }}
                      inputValue={inputValue}
                      onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);
                      }}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      id="combo-box-demo"
                      className="bg-gray-200 appearance-none rounded w-full text-gray-700 leading-tight focus:outline-none focus:bg-white"
                      options={list?.data?.data || []}
                      renderInput={(params) => (
                        <TextField
                          placeholder="Search parent"
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {list.isLoading ? (
                                  <CircularProgress
                                    color="secondary"
                                    size={20}
                                  />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="mb-6 mt-6">
            <label htmlFor="Product Manufacturer" className="label">
              Image <small className="text-red-700">*</small>
            </label>
            <div className="w-52 h- mt-2">
              {imageUploading ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <Spinner />
                  <p className="mt-2">Uploading image...</p>
                </div>
              ) : !imagePreview ? (
                <FileUploader
                  name="category_image"
                  types={fileTypes}
                  handleChange={handleImageUpload}
                  classes="file-uploader"
                  hoverTitle="Drop here"
                />
              ) : (
                <div className="border border-dashed border-blue-700 rounded flex flex-col items-center justify-center h-full p-2">
                  <div className="relative w-full h-36 flex items-center justify-center">
                    <img
                      src={imagePreview}
                      alt="category"
                      className="max-h-36 max-w-full object-contain rounded shadow"
                    />
                  </div>
                  <div className="w-full mt-2 flex flex-col items-center">
                    <input
                      type="text"
                      className="input w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring focus:border-blue-400"
                      style={{ marginTop: 0 }}
                      placeholder="Image Alt Text (optional)"
                      value={imageAlt}
                      onChange={(e) => setImageAlt(e.target.value)}
                    />
                    <button
                      type="button"
                      className="mt-2 text-blue-600 underline text-xs"
                      onClick={handleReUpload}
                    >
                      Re-Upload
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="-ml-4 mb-6">
            <Controller
              name="primary_menu"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div>
                  <SwitchToggle
                    checkedValue={value}
                    label={"Enable primary menu"}
                    onChange={(e) => onChange(e)}
                    name="primary_menu"
                  />
                </div>
              )}
            />
          </div>
          <div className="-ml-4 mb-6">
            <Controller
              name="carousel_menu"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div>
                  <SwitchToggle
                    checkedValue={value}
                    label={"Enable carousel menu"}
                    onChange={(e) => onChange(e)}
                    name="carousel_menu"
                  />
                </div>
              )}
            />
          </div>
          <div className="-ml-4 mb-6">
            <Controller
              name="show_in_category"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div>
                  <SwitchToggle
                    checkedValue={value}
                    label={"Show in category"}
                    onChange={(e) => onChange(e)}
                    name="show_in_category"
                  />
                </div>
              )}
            />
          </div>
        </div>
        {/* {mode === "edit" &&
                    <div>
                        <div className="mb-6 mt-6">
                            <label htmlFor="Product Manufacturer" className='label'>Image </label>
                            <div className='w-52 h-52 mt-2'>
                                <img src={singleCategoryData?.data?.data?.image_name} alt="product image" className='w-full h-full' />
                            </div>
                        </div>
                    </div>} */}
        <>
          {/* SEO */}
          <div className="border-b-2 border-black pt-2 pb-2">
            <span className="text-2xl font-normal">
              Search Engine Optimization
            </span>
          </div>
          <div>
            <div className="pt-6 pb-6  space-y-9">
              <Controller
                name="seo.meta_title"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Input
                    label={"Meta Title"}
                    placeholder={"Meta title"}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />

              <Controller
                name="seo.meta_keywords"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Input
                    label={"Meta Keywords"}
                    placeholder={"meta keywords"}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />

              <Controller
                name="seo.meta_desc"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Input
                    label={"Meta Description"}
                    placeholder={"meta description"}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>
        </>
      </form>
    </>
  );
});

export default CategoryForm;
